export interface HostNotifyArgs {
  title: string;
  body?: string;
  url?: string;
  tag?: string;
}

/**
 * Show an OS-level notification when:
 *   - permission is granted, AND
 *   - the document does not currently have focus.
 *
 * Returns true when a notification was actually fired. Callers fall back to
 * an in-app toast when this returns false (no permission, or window focused).
 *
 * Prefers the active service-worker registration so installed PWAs can resume
 * the tab via notificationclick. Falls back to `new Notification(...)` when
 * SW isn't available (e.g. dev mode, where we don't register).
 */
export async function showHostNotification(args: HostNotifyArgs): Promise<boolean> {
  const log = (reason: string, ok: boolean) => {
    if (import.meta.env.DEV) console.log(`[host-notify] ${ok ? '✓ fired' : '✗ skipped'} — ${reason}`, args.title);
  };

  if (typeof Notification === 'undefined') {
    log('Notification API unavailable', false);
    return false;
  }
  if (Notification.permission !== 'granted') {
    log(`permission=${Notification.permission}`, false);
    return false;
  }
  if (typeof document !== 'undefined' && document.hasFocus()) {
    log('window focused — toast fallback', false);
    return false;
  }

  const options: NotificationOptions = {
    body: args.body,
    icon: '/icons/pwa-192.png',
    badge: '/icons/pwa-192.png',
    tag: args.tag,
    data: { url: args.url ?? '/' },
  };

  // Prefer `new Notification()` for in-app fires — it displays as a regular
  // OS banner immediately. macOS Chrome occasionally swallows the
  // `registration.showNotification()` (persistent) variant when the tab is
  // open, even with permission granted. The persistent path is reserved as a
  // fallback (and remains the path used by real Web Push events from the
  // backend, which are delivered into the SW with no foreground tab).
  try {
    const n = new Notification(args.title, options);
    n.onclick = () => {
      window.focus();
      if (args.url) window.location.href = args.url;
      n.close();
    };
    log('via Notification ctor', true);
    return true;
  } catch (err) {
    log(`ctor failed: ${(err as Error).message} — falling back to SW`, false);
  }

  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.showNotification(args.title, options);
        log('via service worker (fallback)', true);
        return true;
      }
    }
    return false;
  } catch (err) {
    log(`SW path error: ${(err as Error).message}`, false);
    return false;
  }
}
